import { useContext, useMemo, useSyncExternalStore } from "react";
import { TranslationContext } from "./provider";

export function useTranslation() {
  const store = useContext(TranslationContext);
  // Subscribing (rather than reading the locale straight off the store) is what
  // keeps every consumer reactive, including the many that are not wrapped in
  // `observer`. It also means a locale change re-renders consumers that only
  // call `t` — which is the point: `t`'s output depends on the locale.
  // The server snapshot is the store's default: `hydratePersistedLocale` runs in
  // a client effect, so SSR always renders "en" (<html> carries
  // suppressHydrationWarning for the swap).
  // The type argument is explicit: inferring it from both snapshot callbacks
  // widens the locale to `string`, which every consumer feeds back into
  // locale-typed props.
  const locale = useSyncExternalStore<"en" | "fr">(
    store.subscribe,
    () => store.currentLocale,
    () => "en",
  );
  // `t` takes a new identity when the locale changes, and only then. A
  // `useMemo` that builds translated text (column headers, option labels) lists
  // `t` in its deps, and with a `t` that never changed it kept the old
  // language's strings after a switch (NG-2576). Keying `t` on the locale
  // rebuilds every such memo without each one having to list `locale` too.
  //
  // So an effect must NOT list `t` (nor a useCallback that an effect lists): it
  // would re-run on a language switch, re-fetching, resetting a dialog mid-edit
  // or re-flushing a save. It never needs to, because `store.t` reads the
  // current locale when called, so a `t` from an earlier render still
  // translates in the language on screen. `local/no-translate-in-effect-deps`
  // enforces this.
  const t = useMemo(
    () => (key: string, values?: Record<string, unknown>, fallback?: string) =>
      store.t(key, values, fallback),
    // `locale` is read by `store.t`, not here: it is a dep on purpose.
    [store, locale],
  );
  const setLocale = useMemo(() => (next: "en" | "fr") => store.setLocale(next), [store]);
  return { t, setLocale, locale };
}
